import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { AZURE } from '../../services/api';
import { VUKZ } from '../../theme/vukzTheme';
import {
  StudyBreadcrumb,
  StudyHeroSection,
  StudyChartBlock,
  StudyInsightCallout,
  StudyStatCards,
  StudyDataTable,
  StudyMethodologyBox,
  StudyConclusionBox,
  StudyFooter,
} from '../../components/studies';
import {
  BarChart3, LineChart as LineChartIcon, Users, Clock, Trophy,
  Plus, Trash2, ChevronDown, ChevronUp, Play, RefreshCw,
  Download, Sparkles, ArrowUpDown, Filter, Layers,
} from 'lucide-react';
import {
  getSwimCategoryColor,
  sortSwimCategories,
  SWIM_CATEGORY_DISPLAY,
} from '../../config/sportsConfig';

// ─── Constants ────────────────────────────────────────────

const GENDER_LABELS = { M: 'Masculino', F: 'Feminino' };
const GENDER_COLORS = { M: '#3b82f6', F: '#ec4899' };

const CHART_TYPES = [
  { value: 'avg-times', label: 'Tempo Médio por Categoria', icon: '📊', endpoint: 'avg-times-evolution' },
  { value: 'time-evolution', label: 'Evolução Individual', icon: '📈', endpoint: 'time-evolution' },
  { value: 'gender-comparison', label: 'Comparação M vs F', icon: '⚖️', endpoint: 'gender-comparison' },
  { value: 'competition-summary', label: 'Panorama Competições', icon: '🏆', endpoint: 'competition-summary' },
];

const formatTime = (seconds) => {
  if (!seconds || seconds <= 0) return '—';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins > 0) return `${mins}:${secs.toFixed(2).padStart(5, '0')}`;
  return `${secs.toFixed(2)}s`;
};

const getColor = (key, index) =>
  getSwimCategoryColor(key) || GENDER_COLORS[key] || [
    '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
    '#06b6d4', '#ec4899', '#f97316', '#14b8a6', '#a855f7',
  ][index % 10];

// ─── Auto-interpretation generator ────────────────────────

const autoInterpret = (chartType, data, params) => {
  const count = Array.isArray(data) ? data.length : 0;
  if (!count) {
    return {
      question: 'Sem dados disponíveis para a seleção atual.',
      whatItShows: 'Tente ajustar os filtros — pode ser que a combinação selecionada não tenha resultados no banco.',
      whatStandsOut: null,
      practicalMeaning: null,
    };
  }

  if (chartType === 'avg-times') {
    return {
      question: `Como evoluíram os tempos médios ${params.stroke ? `de ${params.stroke}` : ''} ${params.distance ? `${params.distance}m` : ''} ao longo dos anos?`,
      whatItShows: `${count} registros encontrados. Cada linha representa uma categoria etária, mostrando o tempo médio por ano de competição.`,
      whatStandsOut: 'Observe se alguma categoria tem queda consistente (melhora) ou estagnação nos tempos.',
      practicalMeaning: 'Tempos caindo ano a ano indicam que o nível técnico dos atletas está subindo — boa notícia para a natação brasileira.',
    };
  }
  if (chartType === 'time-evolution') {
    return {
      question: `Como os nadadores evoluíram individualmente ${params.stroke ? `nos ${params.distance || ''}m ${params.stroke}` : 'nesta prova'}?`,
      whatItShows: `${count} resultados de nadadores em competições. Cada linha representa um atleta ao longo do tempo.`,
      whatStandsOut: 'Procure atletas com queda contínua de tempos — isso indica um pipeline de desenvolvimento saudável.',
      practicalMeaning: 'Treinadores podem identificar se os atletas estão evoluindo em ritmo compatível com calendário competitivo.',
    };
  }
  if (chartType === 'gender-comparison') {
    return {
      question: `Qual a diferença de desempenho entre masculino e feminino ${params.stroke ? `nos ${params.distance || ''}m ${params.stroke}` : ''}?`,
      whatItShows: `Comparação direta de tempos médios entre gêneros. O gap internacional típico é de 8-11%.`,
      whatStandsOut: 'Compare com o gap internacional: gaps muito acima de 12% podem indicar sub-representação feminina.',
      practicalMeaning: 'Monitorar o gap de gênero ajuda a entender se as políticas de inclusão estão gerando impacto.',
    };
  }
  if (chartType === 'competition-summary') {
    return {
      question: 'Como se comparam as competições em termos de tamanho, diversidade e nível técnico?',
      whatItShows: `${count} competições analisadas com métricas de atletas, clubes, provas e recordes.`,
      whatStandsOut: 'Competições com alto número de atletas e recordes são indicadores de calendário competitivo forte.',
      practicalMeaning: 'Dados que ajudam federações a avaliar o impacto de cada competição no desenvolvimento do esporte.',
    };
  }

  return { question: '', whatItShows: '', whatStandsOut: null, practicalMeaning: null };
};

// ═══════════════════════════════════════════════════════════
// StudyBuilder — Main Component
// ═══════════════════════════════════════════════════════════

const StudyBuilder = () => {
  // Global filters (available options from API)
  const [availableFilters, setAvailableFilters] = useState(null);
  const [loadingFilters, setLoadingFilters] = useState(true);

  // Study blocks — each block is one chart
  const [blocks, setBlocks] = useState([
    { id: 1, chartType: 'avg-times', filters: {}, data: null, loading: false, error: null, expanded: true },
  ]);
  const [nextId, setNextId] = useState(2);

  // Fetch available filters on mount
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        // Fetch from avg-times-evolution endpoint which returns available_filters
        const { data: resp } = await AZURE.get('/editorial/studies/avg-times-evolution', {
          params: { distance: 50 }, // lightweight request to get filter options
        });
        if (resp.success && resp.data?.available_filters) {
          setAvailableFilters(resp.data.available_filters);
        }
      } catch (err) {
        console.error('Error fetching filters:', err);
      } finally {
        setLoadingFilters(false);
      }
    };
    fetchFilters();
  }, []);

  // ─── Block management ─────────────────────────────────

  const addBlock = () => {
    setBlocks((prev) => [
      ...prev,
      { id: nextId, chartType: 'avg-times', filters: {}, data: null, loading: false, error: null, expanded: true },
    ]);
    setNextId((n) => n + 1);
  };

  const removeBlock = (id) => {
    setBlocks((prev) => prev.filter((b) => b.id !== id));
  };

  const updateBlock = (id, updates) => {
    setBlocks((prev) => prev.map((b) => (b.id === id ? { ...b, ...updates } : b)));
  };

  const toggleExpand = (id) => {
    setBlocks((prev) => prev.map((b) => (b.id === id ? { ...b, expanded: !b.expanded } : b)));
  };

  // ─── Fetch data for a single block ────────────────────

  const fetchBlockData = useCallback(async (block) => {
    const chartDef = CHART_TYPES.find((c) => c.value === block.chartType);
    if (!chartDef) return;

    updateBlock(block.id, { loading: true, error: null });

    try {
      const params = {};
      if (block.filters.distance) params.distance = block.filters.distance;
      if (block.filters.stroke) params.stroke = block.filters.stroke;
      if (block.filters.gender) params.gender = block.filters.gender;
      if (block.filters.age_category) params.age_category = block.filters.age_category;
      if (block.filters.year_from) params.year_from = block.filters.year_from;
      if (block.filters.year_to) params.year_to = block.filters.year_to;
      if (block.filters.event_name) params.event_name = block.filters.event_name;
      if (block.filters.top_n) params.top_n = block.filters.top_n;

      const { data: resp } = await AZURE.get(`/editorial/studies/${chartDef.endpoint}`, { params });

      if (resp.success) {
        updateBlock(block.id, { data: resp.data, loading: false });
      } else {
        updateBlock(block.id, { error: 'Erro ao carregar dados.', loading: false });
      }
    } catch (err) {
      console.error(`Error fetching block ${block.id}:`, err);
      updateBlock(block.id, { error: 'Erro de conexão. Tente novamente.', loading: false });
    }
  }, []);

  // Fetch all blocks
  const fetchAll = () => {
    blocks.forEach((b) => fetchBlockData(b));
  };

  // ─── Summary stats ────────────────────────────────────

  const summaryStats = useMemo(() => {
    let totalPoints = 0;
    let totalSeries = 0;
    const yearsSet = new Set();

    blocks.forEach((b) => {
      if (!b.data) return;
      const series = b.data.series || b.data.competitions || [];
      totalSeries += 1;
      totalPoints += series.length;
      series.forEach((r) => {
        const y = r.competition_year || r.year;
        if (y) yearsSet.add(y);
      });
    });

    const years = Array.from(yearsSet).sort();
    return {
      totalBlocks: blocks.length,
      totalPoints,
      totalSeries,
      yearRange: years.length ? `${years[0]}–${years[years.length - 1]}` : '—',
    };
  }, [blocks]);

  // ═══════════════════════════════════════════════════════
  // Render
  // ═══════════════════════════════════════════════════════

  return (
    <div className="min-h-screen" style={{ backgroundColor: VUKZ.bg }}>
      {/* Breadcrumb */}
      <StudyBreadcrumb studyTitle="Construtor de Estudos" />

      {/* Hero */}
      <StudyHeroSection
        emoji="🔬"
        title="Construtor de Estudos"
        description="Monte seu próprio estudo interativo. Escolha competição, ano, prova, gênero e categoria — combine quantos gráficos quiser e compare dados reais da natação brasileira."
        category="natacao"
        backLink="/governance/content"
        backLabel="Voltar para Content Hub"
        stats={[
          { value: summaryStats.totalBlocks, label: 'Gráficos' },
          { value: summaryStats.totalPoints, label: 'Dados carregados' },
          { value: summaryStats.yearRange, label: 'Período' },
        ]}
      />

      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        {/* Instructions callout */}
        <StudyInsightCallout variant="insight" title="Como usar o Construtor">
          <div className="space-y-1.5 text-sm text-gray-600">
            <p><strong>1.</strong> Escolha o tipo de gráfico que você quer ver (tempo médio, evolução individual, etc.)</p>
            <p><strong>2.</strong> Use os filtros para selecionar distância, estilo, gênero e categoria</p>
            <p><strong>3.</strong> Clique em <strong>"Carregar Dados"</strong> para buscar os resultados</p>
            <p><strong>4.</strong> Adicione quantos gráficos quiser com o botão <strong>"+ Adicionar Gráfico"</strong></p>
          </div>
        </StudyInsightCallout>

        {/* Global action bar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={fetchAll}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold rounded-xl transition-all"
              style={{ backgroundColor: VUKZ.primary, color: '#fff' }}
            >
              <Play className="w-4 h-4" />
              Carregar Todos
            </button>
            <button
              onClick={addBlock}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold border border-gray-300 rounded-xl hover:bg-gray-50 transition-all"
            >
              <Plus className="w-4 h-4" />
              Adicionar Gráfico
            </button>
          </div>
          <span className="text-xs text-gray-400">
            {blocks.length} {blocks.length === 1 ? 'gráfico' : 'gráficos'}
          </span>
        </div>

        {/* Study blocks */}
        <div className="space-y-6">
          {blocks.map((block, idx) => (
            <StudyBlock
              key={block.id}
              block={block}
              index={idx}
              availableFilters={availableFilters}
              loadingFilters={loadingFilters}
              onUpdate={(updates) => updateBlock(block.id, updates)}
              onFetch={() => fetchBlockData(block)}
              onRemove={() => removeBlock(block.id)}
              onToggle={() => toggleExpand(block.id)}
              canRemove={blocks.length > 1}
            />
          ))}
        </div>

        {/* Add another CTA */}
        <button
          onClick={addBlock}
          className="w-full py-4 border-2 border-dashed border-gray-200 rounded-2xl text-gray-400 hover:border-gray-400 hover:text-gray-600 transition-all flex items-center justify-center gap-2 text-sm font-bold"
        >
          <Plus className="w-5 h-5" />
          Adicionar outro gráfico ao estudo
        </button>

        {/* Methodology */}
        <StudyMethodologyBox
          methodology="Este construtor de estudos permite criar análises personalizadas combinando dados reais de competições de natação registradas no ArenaStats. Os tempos são calculados a partir de resultados válidos (excluindo DNS, DNF, DQ e revezamentos). Outliers (>3σ) são incluídos mas sinalizados via CV%."
          dataSource="ArenaStats — Competições oficiais de natação (banco de dados)"
          limitations={[
            'A cobertura depende das competições já inseridas no sistema.',
            'Categorias de base podem ter menos registros, impactando médias.',
            'Revezamentos são excluídos pois usam partida lançada (flying start).',
          ]}
        />

        {/* Footer */}
        <StudyFooter />
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// StudyBlock — Individual chart builder block
// ═══════════════════════════════════════════════════════════

const StudyBlock = ({
  block,
  index,
  availableFilters,
  loadingFilters,
  onUpdate,
  onFetch,
  onRemove,
  onToggle,
  canRemove,
}) => {
  const chartDef = CHART_TYPES.find((c) => c.value === block.chartType);

  const handleFilterChange = (key, value) => {
    onUpdate({ filters: { ...block.filters, [key]: value || undefined } });
  };

  // ─── Transform data for chart rendering ───────────────

  const chartRender = useMemo(() => {
    if (!block.data) return null;

    const series = block.data.series || [];
    const competitions = block.data.competitions || [];
    const genderGap = block.data.gender_gap_analysis || [];

    // === AVG TIMES: group by event+gender, pivot categories into series ===
    if (block.chartType === 'avg-times') {
      const groups = {};
      series.forEach((row) => {
        const key = `${row.event_name}__${row.gender}`;
        if (!groups[key]) {
          groups[key] = {
            event_name: row.event_name,
            gender: row.gender,
            distance_meters: row.distance_meters,
            stroke: row.stroke,
            categories: new Set(),
            byYear: {},
          };
        }
        groups[key].categories.add(row.age_category);
        if (!groups[key].byYear[row.competition_year]) {
          groups[key].byYear[row.competition_year] = { year: row.competition_year };
        }
        groups[key].byYear[row.competition_year][row.age_category] = row.avg_time_seconds;
      });

      return Object.values(groups).map((g) => {
        const cats = Array.from(g.categories).sort();
        const chartData = Object.values(g.byYear).sort((a, b) => a.year - b.year);
        const chartSeries = cats.map((cat, i) => ({
          dataKey: cat,
          name: cat,
          color: getColor(cat, i),
        }));
        const tableColumns = [
          { key: 'year', label: 'Ano', align: 'left' },
          ...cats.map((cat) => ({
            key: cat,
            label: cat,
            color: getColor(cat, 0),
            format: (v) => (v ? formatTime(v) : '—'),
          })),
        ];
        const title = `${g.event_name} — ${GENDER_LABELS[g.gender] || g.gender}`;
        return { type: 'line', title, chartData, chartSeries, tableColumns, reversedY: true };
      });
    }

    // === TIME EVOLUTION: group by swimmer, show individual lines ===
    if (block.chartType === 'time-evolution') {
      const swimmers = {};
      series.forEach((row) => {
        const key = `${row.swimmer_id}__${row.event_name}`;
        if (!swimmers[key]) {
          swimmers[key] = {
            swimmer_name: row.swimmer_display_name || row.swimmer_name,
            event_name: row.event_name,
            data: [],
          };
        }
        swimmers[key].data.push({
          year: row.competition_year,
          time: row.time_seconds,
          competition: row.competition_name,
        });
      });

      // Group by event, max 10 swimmers per chart
      const byEvent = {};
      Object.values(swimmers).forEach((sw) => {
        if (!byEvent[sw.event_name]) byEvent[sw.event_name] = [];
        byEvent[sw.event_name].push(sw);
      });

      return Object.entries(byEvent).map(([eventName, swList]) => {
        const top = swList.slice(0, 10);
        // Pivot: rows = years, columns = swimmer names
        const yearsMap = {};
        top.forEach((sw) => {
          sw.data.forEach((d) => {
            if (!yearsMap[d.year]) yearsMap[d.year] = { year: d.year };
            yearsMap[d.year][sw.swimmer_name] = d.time;
          });
        });
        const chartData = Object.values(yearsMap).sort((a, b) => a.year - b.year);
        const chartSeries = top.map((sw, i) => ({
          dataKey: sw.swimmer_name,
          name: sw.swimmer_name,
          color: getColor(sw.swimmer_name, i),
        }));
        const tableColumns = [
          { key: 'year', label: 'Ano', align: 'left' },
          ...top.map((sw) => ({
            key: sw.swimmer_name,
            label: sw.swimmer_name,
            format: (v) => (v ? formatTime(v) : '—'),
          })),
        ];
        return {
          type: 'line',
          title: `Evolução Individual — ${eventName}`,
          chartData,
          chartSeries,
          tableColumns,
          reversedY: true,
        };
      });
    }

    // === GENDER COMPARISON: side-by-side bars ===
    if (block.chartType === 'gender-comparison') {
      if (genderGap.length) {
        // Group by event
        const byEvent = {};
        genderGap.forEach((row) => {
          if (!byEvent[row.event_name]) byEvent[row.event_name] = [];
          byEvent[row.event_name].push(row);
        });

        return Object.entries(byEvent).map(([eventName, rows]) => {
          const chartData = rows
            .sort((a, b) => a.competition_year - b.competition_year)
            .map((r) => ({
              year: r.competition_year,
              Masculino: r.male_avg,
              Feminino: r.female_avg,
              'Gap (%)': r.gap_percent,
            }));
          const chartSeries = [
            { dataKey: 'Masculino', name: 'Masculino', color: GENDER_COLORS.M },
            { dataKey: 'Feminino', name: 'Feminino', color: GENDER_COLORS.F },
          ];
          const tableColumns = [
            { key: 'year', label: 'Ano', align: 'left' },
            { key: 'Masculino', label: 'Masc.', format: (v) => formatTime(v) },
            { key: 'Feminino', label: 'Fem.', format: (v) => formatTime(v) },
            { key: 'Gap (%)', label: 'Gap %', format: (v) => (v != null ? `${v}%` : '—') },
          ];
          return { type: 'bar', title: `${eventName} — M vs F`, chartData, chartSeries, tableColumns, reversedY: true };
        });
      }
      // Fallback: use series data grouped by event+year
      const byEvent = {};
      series.forEach((row) => {
        const key = `${row.event_name}__${row.competition_year}`;
        if (!byEvent[key]) byEvent[key] = {};
        byEvent[key][row.gender] = row;
        byEvent[key]._event = row.event_name;
        byEvent[key]._year = row.competition_year;
      });
      const events = {};
      Object.values(byEvent).forEach((pair) => {
        const ev = pair._event;
        if (!events[ev]) events[ev] = [];
        events[ev].push({
          year: pair._year,
          Masculino: pair.M?.avg_time_seconds,
          Feminino: pair.F?.avg_time_seconds,
        });
      });
      return Object.entries(events).map(([ev, rows]) => {
        const chartData = rows.sort((a, b) => a.year - b.year);
        const chartSeries = [
          { dataKey: 'Masculino', name: 'Masculino', color: GENDER_COLORS.M },
          { dataKey: 'Feminino', name: 'Feminino', color: GENDER_COLORS.F },
        ];
        const tableColumns = [
          { key: 'year', label: 'Ano', align: 'left' },
          { key: 'Masculino', label: 'Masc.', format: (v) => formatTime(v) },
          { key: 'Feminino', label: 'Fem.', format: (v) => formatTime(v) },
        ];
        return { type: 'bar', title: `${ev} — M vs F`, chartData, chartSeries, tableColumns, reversedY: true };
      });
    }

    // === COMPETITION SUMMARY: bar chart of competitions ===
    if (block.chartType === 'competition-summary') {
      if (!competitions.length) return [];
      const chartData = competitions.map((c) => ({
        name: c.competition_name?.substring(0, 30) || `Comp ${c.competition_id}`,
        Atletas: c.total_swimmers || 0,
        Clubes: c.total_clubs || 0,
        Provas: c.total_events || 0,
        Recordes: (c.world_records || 0) + (c.national_records || 0) + (c.event_records || 0),
        year: c.year,
      }));
      const chartSeries = [
        { dataKey: 'Atletas', name: 'Atletas', color: '#3b82f6' },
        { dataKey: 'Clubes', name: 'Clubes', color: '#10b981' },
        { dataKey: 'Provas', name: 'Provas', color: '#f59e0b' },
      ];
      const tableColumns = [
        { key: 'name', label: 'Competição', align: 'left' },
        { key: 'year', label: 'Ano' },
        { key: 'Atletas', label: 'Atletas' },
        { key: 'Clubes', label: 'Clubes' },
        { key: 'Provas', label: 'Provas' },
        { key: 'Recordes', label: 'Recordes' },
      ];
      return [{ type: 'bar', title: 'Panorama das Competições', chartData, chartSeries, tableColumns, reversedY: false, xField: 'name' }];
    }

    return [];
  }, [block.data, block.chartType]);

  const interp = autoInterpret(block.chartType, block.data?.series || block.data?.competitions || [], block.filters);

  // ─── Render ───────────────────────────────────────────

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{chartDef?.icon || '📊'}</span>
          <div>
            <h3 className="font-bold text-gray-900">
              Gráfico {index + 1}: {chartDef?.label || 'Personalizado'}
            </h3>
            <p className="text-xs text-gray-400">
              {block.data ? `${(block.data.series || block.data.competitions || []).length} registros` : 'Sem dados'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onToggle}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
          >
            {block.expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {canRemove && (
            <button
              onClick={onRemove}
              className="p-2 text-gray-300 hover:text-red-500 rounded-lg transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {block.expanded && (
        <div className="p-6 space-y-5">
          {/* Chart type selector */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-2">
              <Layers className="w-3.5 h-3.5 inline mr-1" />
              Tipo de Gráfico
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {CHART_TYPES.map((ct) => (
                <button
                  key={ct.value}
                  onClick={() => onUpdate({ chartType: ct.value, data: null })}
                  className={`px-3 py-2.5 rounded-xl border text-xs font-bold transition-all text-left ${
                    block.chartType === ct.value
                      ? 'border-gray-900 bg-gray-900 text-white'
                      : 'border-gray-200 hover:border-gray-400 text-gray-600'
                  }`}
                >
                  <span className="text-base mr-1.5">{ct.icon}</span>
                  {ct.label}
                </button>
              ))}
            </div>
          </div>

          {/* Filters */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-2">
              <Filter className="w-3.5 h-3.5 inline mr-1" />
              Filtros
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {/* Distance */}
              <FilterSelect
                label="Distância"
                value={block.filters.distance || ''}
                onChange={(v) => handleFilterChange('distance', v)}
                options={
                  availableFilters?.distances?.map((d) => ({ value: d, label: `${d}m` })) || []
                }
                loading={loadingFilters}
              />
              {/* Stroke */}
              <FilterSelect
                label="Estilo"
                value={block.filters.stroke || ''}
                onChange={(v) => handleFilterChange('stroke', v)}
                options={
                  availableFilters?.strokes?.map((s) => ({ value: s, label: s })) || []
                }
                loading={loadingFilters}
              />
              {/* Gender */}
              <FilterSelect
                label="Gênero"
                value={block.filters.gender || ''}
                onChange={(v) => handleFilterChange('gender', v)}
                options={
                  availableFilters?.genders?.map((g) => ({ value: g, label: GENDER_LABELS[g] || g })) || []
                }
                loading={loadingFilters}
              />
              {/* Age Category */}
              <FilterSelect
                label="Categoria"
                value={block.filters.age_category || ''}
                onChange={(v) => handleFilterChange('age_category', v)}
                options={
                  availableFilters?.age_categories?.map((c) => ({ value: c, label: c })) || []
                }
                loading={loadingFilters}
              />
              {/* Year From */}
              <FilterSelect
                label="De (ano)"
                value={block.filters.year_from || ''}
                onChange={(v) => handleFilterChange('year_from', v)}
                options={
                  availableFilters?.years?.map((y) => ({ value: y, label: String(y) })) || []
                }
                loading={loadingFilters}
              />
              {/* Year To */}
              <FilterSelect
                label="Até (ano)"
                value={block.filters.year_to || ''}
                onChange={(v) => handleFilterChange('year_to', v)}
                options={
                  availableFilters?.years?.map((y) => ({ value: y, label: String(y) })) || []
                }
                loading={loadingFilters}
              />
            </div>

            {/* Event name (text input for time-evolution) */}
            {(block.chartType === 'time-evolution' || block.chartType === 'gender-comparison') && (
              <div className="mt-3">
                <label className="block text-xs font-semibold text-gray-500 mb-1">Prova (nome)</label>
                <input
                  type="text"
                  placeholder="Ex: 50m Livre, 100m Costas..."
                  value={block.filters.event_name || ''}
                  onChange={(e) => handleFilterChange('event_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-gray-200 focus:outline-none"
                />
              </div>
            )}
          </div>

          {/* Fetch button */}
          <div className="flex items-center gap-3">
            <button
              onClick={onFetch}
              disabled={block.loading}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-bold rounded-xl transition-all disabled:opacity-50"
              style={{ backgroundColor: VUKZ.primary, color: '#fff' }}
            >
              {block.loading ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4" />
              )}
              {block.loading ? 'Carregando...' : 'Carregar Dados'}
            </button>
            {block.data && (
              <span className="text-xs text-green-600 font-semibold">
                ✓ {(block.data.series || block.data.competitions || []).length} registros
              </span>
            )}
          </div>

          {/* Error state */}
          {block.error && (
            <div className="text-sm text-red-500 bg-red-50 px-4 py-3 rounded-xl">{block.error}</div>
          )}

          {/* Charts */}
          {block.data && chartRender && chartRender.length > 0 && (
            <div className="space-y-6">
              {chartRender.map((chart, ci) => (
                <StudyChartBlock
                  key={ci}
                  chartType={chart.type}
                  title={chart.title}
                  question={ci === 0 ? interp.question : undefined}
                  data={chart.chartData}
                  series={chart.chartSeries}
                  xField={chart.xField || 'year'}
                  formatY={chart.reversedY ? formatTime : undefined}
                  formatTooltip={
                    chart.reversedY
                      ? (value, name) => [formatTime(value), name]
                      : undefined
                  }
                  reversedY={chart.reversedY}
                  whatItShows={ci === 0 ? interp.whatItShows : undefined}
                  whatStandsOut={ci === 0 ? interp.whatStandsOut : undefined}
                  practicalMeaning={ci === 0 ? interp.practicalMeaning : undefined}
                >
                  <StudyDataTable
                    data={chart.chartData}
                    columns={chart.tableColumns}
                    caption={chart.title}
                  />
                </StudyChartBlock>
              ))}
            </div>
          )}

          {/* Empty state after fetch */}
          {block.data && (!chartRender || chartRender.length === 0) && (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <p className="text-gray-400 text-lg font-bold">Nenhum dado encontrado</p>
              <p className="text-gray-300 text-sm mt-1">Tente ajustar os filtros ou escolher outro tipo de gráfico.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── FilterSelect — tiny inline filter dropdown ─────────

const FilterSelect = ({ label, value, onChange, options = [], loading }) => (
  <div>
    <label className="block text-[11px] font-semibold text-gray-400 mb-1">{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={loading}
      className="w-full px-2.5 py-2 border border-gray-200 rounded-lg text-xs bg-white focus:ring-2 focus:ring-gray-200 focus:outline-none disabled:bg-gray-50"
    >
      <option value="">Todos</option>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  </div>
);

export default StudyBuilder;
